import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { writingAssistantService } from '@/lib/services/writingAssistantService'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { CreateWritingAssistantData } from '@/lib/services/writingAssistantService'
import type { BobAnalyse } from '@/lib/services/bobAnalyseService'
import { CONTENT_TYPES } from '@/lib/types/writing'
import { formatDateTime } from '@/lib/utils/timeFormat'
import { stripHtmlTagsFromArray } from '@/lib/utils/htmlUtils'
import { Loader2 } from 'lucide-react'

interface WritingAssistantCreateFormProps {
  onSuccess: () => void
  isClosedCrisis: boolean
}

export function WritingAssistantCreateForm({
  onSuccess,
  isClosedCrisis,
}: WritingAssistantCreateFormProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contentType, setContentType] = useState<string>('')
  const [extraInstructions, setExtraInstructions] = useState('')
  const [bobAnalyseId, setBobAnalyseId] = useState<string>('')
  const [bobAnalyses, setBobAnalyses] = useState<BobAnalyse[]>([])
  const [isLoadingBobAnalyses, setIsLoadingBobAnalyses] = useState(false)
  const [availableBoodschappen, setAvailableBoodschappen] = useState<string[]>([])
  const [selectedBoodschappen, setSelectedBoodschappen] = useState<string[]>([])
  const [customBoodschap, setCustomBoodschap] = useState<string>('')
  const [showCustomBoodschap, setShowCustomBoodschap] = useState(false)
  const { activeCrisis } = useCrisisStore()

  // Permission check
  const canCreateWriting = useHasPermission('writing.create')

  // Fetch available BOB analyses when component mounts
  useEffect(() => {
    const fetchBobAnalyses = async () => {
      if (!activeCrisis) return

      setIsLoadingBobAnalyses(true)
      try {
        const analyses = await bobAnalyseService.listBobAnalyses(activeCrisis.id)
        // Filter to only show completed BOB analyses
        const completedAnalyses = analyses.filter(
          bob => bob.status === 'completed' || bob.status === 'ready'
        )
        setBobAnalyses(completedAnalyses)
      } catch (error) {
        console.error('Error fetching BOB analyses:', error)
        setBobAnalyses([])
      } finally {
        setIsLoadingBobAnalyses(false)
      }
    }

    fetchBobAnalyses()
  }, [activeCrisis])

  // Extract Boodschap values when BOB analyse is selected
  useEffect(() => {
    if (!bobAnalyseId) {
      setAvailableBoodschappen([])
      setSelectedBoodschappen([])
      setCustomBoodschap('')
      setShowCustomBoodschap(false)
      return
    }

    const selectedBobAnalyse = bobAnalyses.find(bob => bob.id === bobAnalyseId)
    if (selectedBobAnalyse && selectedBobAnalyse.oordeelsvorming) {
      const boodschappen = selectedBobAnalyse.oordeelsvorming['Kernboodschappen'] || []
      // Sanitize HTML tags from boodschappen
      const sanitizedBoodschappen = stripHtmlTagsFromArray(boodschappen)
      setAvailableBoodschappen(sanitizedBoodschappen)
      // Pre-select all by default
      setSelectedBoodschappen(sanitizedBoodschappen)
    } else {
      setAvailableBoodschappen([])
      setSelectedBoodschappen([])
    }
    // Reset custom boodschap when BOB analyse changes
    setCustomBoodschap('')
    setShowCustomBoodschap(false)
  }, [bobAnalyseId, bobAnalyses])

  const handleBoodschapToggle = (boodschap: string) => {
    setSelectedBoodschappen(prev => {
      if (prev.includes(boodschap)) {
        return prev.filter(b => b !== boodschap)
      } else {
        return [...prev, boodschap]
      }
    })
  }

  const handleSubmit = async () => {
    if (!contentType) {
      return
    }

    setIsSubmitting(true)

    try {
      const data: CreateWritingAssistantData = {
        contentType,
      }
      if (extraInstructions) {
        data.extraInstructions = extraInstructions
      }
      if (bobAnalyseId) {
        data.bobAnalyseId = bobAnalyseId
      }
      // Always include boodschappen (from BOB analyse or custom)
      const allBoodschappen = [...selectedBoodschappen]
      if (customBoodschap.trim()) {
        allBoodschappen.push(customBoodschap.trim())
      }
      if (allBoodschappen.length > 0) {
        data.selectedBoodschappen = allBoodschappen
      }
      await writingAssistantService.createWritingAssistant(data)

      // Reset form
      setContentType('')
      setExtraInstructions('')
      setBobAnalyseId('')
      setSelectedBoodschappen([])
      setAvailableBoodschappen([])
      setCustomBoodschap('')
      setShowCustomBoodschap(false)

      onSuccess()
    } catch (error) {
      console.error('Error creating writing assistant:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle>{t('writing.nieuwWriting.titel')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="contentType">
            {t('writing.nieuwWriting.contentType')} <span className="text-red-500">*</span>
          </Label>
          <Select
            value={contentType}
            onValueChange={setContentType}
            disabled={isClosedCrisis || !canCreateWriting}
          >
            <SelectTrigger id="contentType">
              <SelectValue placeholder={t('writing.nieuwWriting.contentTypePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {CONTENT_TYPES.map(type => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="bobAnalyse">BOB-GOGME Analyse (optioneel)</Label>
          <Select
            value={bobAnalyseId || 'none'}
            onValueChange={value => setBobAnalyseId(value === 'none' ? '' : value)}
            disabled={isClosedCrisis || !canCreateWriting || isLoadingBobAnalyses}
          >
            <SelectTrigger id="bobAnalyse">
              <SelectValue placeholder="Selecteer een BOB analyse om te gebruiken als basis" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Geen BOB analyse</SelectItem>
              {bobAnalyses.map((bob, index) => {
                const createdDate = new Date(bob.createdAt)
                const formattedDateTime = formatDateTime(createdDate)
                const analyseNumber = index + 1

                return (
                  <SelectItem key={bob.id} value={bob.id}>
                    Strategie {analyseNumber} | {formattedDateTime}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          {isLoadingBobAnalyses && (
            <p className="text-sm text-muted-foreground">
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
              BOB analyses laden...
            </p>
          )}
          {!isLoadingBobAnalyses && bobAnalyses.length === 0 && (
            <p className="text-sm text-muted-foreground">Geen voltooide BOB analyses beschikbaar</p>
          )}
        </div>

        {/* Boodschap selection - always shown, mandatory */}
        <div className="space-y-3">
          <Label>
            Selecteer Boodschappen <span className="text-red-500">*</span>
          </Label>
          <p className="text-sm text-muted-foreground">
            {bobAnalyseId
              ? 'Kies minimaal één boodschap uit de BOB analyse of voeg een eigen boodschap toe'
              : 'Voeg een eigen boodschap toe'}
          </p>
          <div className="space-y-2 rounded-md border p-3">
            {/* BOB analyse boodschappen - only shown when BOB analyse is selected */}
            {bobAnalyseId && availableBoodschappen.length > 0 && (
              <>
                {availableBoodschappen.map(boodschap => (
                  <div key={boodschap} className="flex items-start space-x-2">
                    <Checkbox
                      id={`boodschap-${boodschap}`}
                      checked={selectedBoodschappen.includes(boodschap)}
                      onCheckedChange={() => handleBoodschapToggle(boodschap)}
                      disabled={isClosedCrisis || !canCreateWriting}
                    />
                    <label
                      htmlFor={`boodschap-${boodschap}`}
                      className="flex-1 cursor-pointer text-sm leading-relaxed"
                    >
                      {boodschap}
                    </label>
                  </div>
                ))}
              </>
            )}

            {/* Anders (Other) option - always shown */}
            <div className="flex items-start space-x-2">
              <Checkbox
                id="boodschap-anders"
                checked={showCustomBoodschap}
                onCheckedChange={checked => {
                  setShowCustomBoodschap(!!checked)
                  if (!checked) {
                    setCustomBoodschap('')
                  }
                }}
                disabled={isClosedCrisis || !canCreateWriting}
              />
              <label
                htmlFor="boodschap-anders"
                className="flex-1 cursor-pointer text-sm font-medium leading-relaxed"
              >
                {bobAnalyseId ? 'Anders (eigen boodschap)' : 'Eigen boodschap'}
              </label>
            </div>

            {/* Custom boodschap input */}
            {showCustomBoodschap && (
              <div className="ml-6 mt-2">
                <Textarea
                  id="customBoodschap"
                  value={customBoodschap}
                  onChange={e => setCustomBoodschap(e.target.value)}
                  placeholder="Typ hier uw eigen boodschap..."
                  rows={3}
                  disabled={isClosedCrisis || !canCreateWriting}
                  className="text-sm"
                />
              </div>
            )}
          </div>
          {bobAnalyseId && availableBoodschappen.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {selectedBoodschappen.length} van {availableBoodschappen.length} geselecteerd
              {customBoodschap.trim() && ' + 1 eigen boodschap'}
            </p>
          )}
          {(!bobAnalyseId || availableBoodschappen.length === 0) && (
            <p className="text-xs text-muted-foreground">
              {customBoodschap.trim() ? '1 eigen boodschap' : 'Geen boodschap geselecteerd'}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="extraInstructions">{t('writing.nieuwWriting.extraInstructies')}</Label>
          <Textarea
            id="extraInstructions"
            value={extraInstructions}
            onChange={e => setExtraInstructions(e.target.value)}
            placeholder={t('writing.nieuwWriting.extraInstructiesPlaceholder')}
            rows={4}
            disabled={isClosedCrisis || !canCreateWriting}
          />
        </div>

        <Button
          variant="gradient"
          onClick={handleSubmit}
          disabled={
            !contentType ||
            (selectedBoodschappen.length === 0 && !customBoodschap.trim()) ||
            isSubmitting ||
            isClosedCrisis ||
            !canCreateWriting
          }
          className="w-full"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isSubmitting
            ? t('writing.nieuwWriting.berichtGenererenBezig')
            : t('writing.nieuwWriting.berichtGenereren')}
        </Button>
      </CardContent>
    </Card>
  )
}
